import React, { useCallback, useEffect, useRef, useState } from "react";
import ButtonsContainer from "./ButtonsContainer";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import { Loading } from "app/components/Wrappers";
import CalendarIco from "app/images/calendar-ico-gray.svg";
import HistoryIco from "app/images/history-ico.svg";
import {
  capitalize,
  formatDateToShortString,
  formatTimeFromISO,
  getDurationInMinutes,
  groupByMonthYear,
} from "./utils";
import ScheduleItem from "./ScheduleItem";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import UserModel from "app/models/UserModel";

interface Props {
  isModalOpen: boolean;
  athleteData: Partial<UserModel>;
}

const Bookings = ({ isModalOpen, athleteData }: Props) => {
  const { t } = useTranslation();

  const schedule_options = [
    { value: "upcoming", label: t("Upcoming") },
    { value: "history", label: t("History") },
  ];

  const [scheduleType, setScheduleType] = useState(schedule_options[0]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [historyEvents, setHistoryEvents] = useState([]);
  const listContainerRef = useRef(null);
  const loaderRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [hasMoreUpcoming, setHasMoreUpcoming] = useState(true);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);

  const isUpcoming = scheduleType.value === "upcoming";
  const currentEvents = isUpcoming ? upcomingEvents : historyEvents;
  const hasMoreEvents = isUpcoming ? hasMoreUpcoming : hasMoreHistory;

  const renderEventGroups = () => {
    if (loading && currentEvents.length === 0) {
      return <Loading isLoading loadType="spinner" />;
    }

    if (currentEvents.length === 0 && !loading) {
      if (isUpcoming) {
        return (
          <div className={styles.no_events_container}>
            <img
              src={CalendarIco}
              alt="calendar"
              className={styles.no_events_icon}
            />
            <p className={styles.no_events_title}>{t("No upcoming events")}</p>
            <p className={styles.no_events_subtitle}>
              {t("All upcoming sessions will be displayed here")}
            </p>
          </div>
        );
      } else {
        return (
          <div className={styles.no_events_container}>
            <img
              src={HistoryIco}
              alt="calendar"
              className={styles.no_events_icon}
            />
            <p className={styles.no_events_title}>{t("No history yet")}</p>
            <p className={styles.no_events_subtitle}>
              {t("All committed sessions will be displayed here")}
            </p>
          </div>
        );
      }
    }

    return groupByMonthYear(currentEvents).map((item: any) => (
      <div key={item.date} className={styles.month_group}>
        <p className={styles.title_text}>{item.date}</p>
        {item.values?.map((value) => (
          <ScheduleItem key={value.id} schedule={value} />
        ))}
      </div>
    ));
  };

  const fetchEvents = useCallback(async () => {
    if (loading || !isModalOpen) {
      return;
    }

    const isUpcoming = scheduleType.value === "upcoming";
    const hasMore = isUpcoming ? hasMoreUpcoming : hasMoreHistory;
    if (!hasMore) {
      return;
    }

    const page = isUpcoming ? upcomingPage : historyPage;
    const endpoint = isUpcoming ? "upcoming_events" : "history_events";
    const setEvents = isUpcoming ? setUpcomingEvents : setHistoryEvents;
    const setPage = isUpcoming ? setUpcomingPage : setHistoryPage;
    const setHasMore = isUpcoming ? setHasMoreUpcoming : setHasMoreHistory;
    setLoading(true);
    try {
      const response = await schedulingFetchGet(
        `student_profiles/${athleteData.externalId || athleteData.athleteId}/${endpoint}`,
        { page },
      );
      const formatted = (response.events ?? []).map((event: any, index: number) => {
        const { coachProfile } = event;
        const isLesson = event.type === "lesson_schedule";
        return {
          id: `${event.id || index}-${page}`,
          type: event.type,
          date: formatDateToShortString(event.start),
          name: isLesson
            ? `${coachProfile.firstName} ${coachProfile.lastName}`
            : `${capitalize(event.attendance)} (${event.category})`,
          profile_image: isLesson
            ? coachProfile.profilePicture
            : event.externalThumbnailUrl,
          title: isLesson ? event.lessonRateTitle : event.title,
          time: formatTimeFromISO(event.start),
          duration: getDurationInMinutes(event.start, event.end),
        };
      });

      setEvents((prev) => [...prev, ...formatted]);
      setPage((prev) => prev + 1);
      setHasMore(response.nextPage !== null);
    } catch (error) {
      console.error("Error fetching events:", error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [
    athleteData.externalId,
    scheduleType.value,
    loading,
    hasMoreUpcoming,
    hasMoreHistory,
    upcomingPage,
    historyPage,
  ]);

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    const isUpcoming = scheduleType.value === "upcoming";
    const events = isUpcoming ? upcomingEvents : historyEvents;

    if (events.length === 0) {
      fetchEvents();
    }
  }, [isModalOpen, scheduleType, fetchEvents, upcomingEvents, historyEvents]);

  useEffect(() => {
    if (!isModalOpen) {
      setUpcomingEvents([]);
      setHistoryEvents([]);
      setUpcomingPage(1);
      setHistoryPage(1);
      setHasMoreUpcoming(true);
      setHasMoreHistory(true);
    }
  }, [isModalOpen]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !loading) {
          if (hasMoreEvents) {
            fetchEvents();
          }
        }
      },
      { threshold: 0.1, rootMargin: "10px" },
    );

    const currentLoaderRef = loaderRef.current;
    if (currentLoaderRef) {
      observer.observe(currentLoaderRef);
    }

    return () => {
      if (currentLoaderRef) {
        observer.unobserve(currentLoaderRef);
      }
    };
  }, [fetchEvents, hasMoreEvents, loading]);

  return (
    <>
      <ButtonsContainer
        items={schedule_options}
        containerStyle={styles.option_button_group}
        itemSelected={scheduleType}
        setItemSelected={setScheduleType}
      />

      <div className={styles.list_container} ref={listContainerRef}>
        {renderEventGroups()}

        {loading && currentEvents.length > 0 && (
          <div className={styles.loading_more}>
            <Loading isLoading loadType="spinner" />
          </div>
        )}

        {hasMoreEvents && (
          <div ref={loaderRef} className={styles.loader_observer_spacer} />
        )}
      </div>
    </>
  );
};

export default Bookings;
